import * as React from 'react';
import { BoxProps, Flex, FormLabel, Input, InputProps } from '@chakra-ui/react';
import { normalizeToFullYear } from '../../../utilities/formatting';
import { IDate } from '../../../types';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { withConfirm } from '../withConfirm';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';
import { Select } from '../Select';
import { months } from 'data';

interface IProps extends DirectionProp {
  value?: Partial<IDate>;
  id?: string;
  name?: string;
  label?: string;
  dobMonthName?: string;
  dobYearName?: string;
  subText?: string;
  inputHelperText?: string;
  errorText?: string;
  hideErrorText?: boolean;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  onChangeMM: (event: React.FormEvent<HTMLSelectElement>) => void;
  onChangeYY: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  inputPropsMM?: InputProps;
  inputPropsYY?: InputProps;
  mb?: BoxProps['mb'];
  isDisabled?: boolean;
}

const MonthDayInput: React.FC<IProps> = (props: IProps) => {
  const {
    value,
    id,
    name,
    label,
    dobMonthName,
    dobYearName,
    subText,
    errorText,
    hideErrorText,
    onChangeMM,
    onChangeYY,
    onBlur,
    onFocus,
    canUndo = false,
    onUndo,
    size = 'lg',
    mb = 0,
    direction,
    inputPropsYY,
    isDisabled = false,
  } = props;

  const onBlurYYYY = (evt: React.FocusEvent<HTMLInputElement>) => {
    let customEvent = evt;
    // Only normalize year if user has entered a value (not empty or just whitespace)
    if (
      evt.currentTarget.value.length > 0 &&
      evt.currentTarget.value.length < 4 &&
      evt.currentTarget.value.trim() !== ''
    ) {
      const year = String(normalizeToFullYear(evt.currentTarget.value));
      customEvent = {
        ...evt,
        currentTarget: {
          ...evt.currentTarget,
          value: year,
        },
      };
      if (onChangeYY) onChangeYY(customEvent);
    }
    if (onBlur) onBlur(customEvent);
  };

  const onChangeDatePart = (
    evt: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLSelectElement>,
    datePart: string,
  ) => {
    // disallow any non numeric keys
    const { value } = evt.currentTarget;

    if (!/^\d*$/.test(value)) {
      return false;
    }
    switch (datePart) {
      case 'MM': {
        return onChangeMM(evt as React.FormEvent<HTMLSelectElement>);
      }
      case 'YY': {
        return onChangeYY(evt as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };
  return (
    <CustomFormControl
      id={id}
      controlProps={{
        isInvalid: !!(!hideErrorText && errorText),
        mb,
      }}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ htmlFor: `${id}-day-input` }}
      subLabel={subText}
      error={errorText}
    >
      <Flex gap={{ base: 1, lg: 2 }} mb={2}>
        <Flex flexDirection="column" flexGrow="1" width="100%" gap={0.5}>
          <Select
            id={`${id}-month-input`}
            name={dobMonthName ?? `${name}-month-input`}
            key="month-input"
            data-selector={`${id}month-input-field`}
            value={value && value.MM !== null ? value.MM : ''}
            options={months.map(m => ({ label: m.name, value: m.value }))}
            onChange={(evt: React.FormEvent<HTMLSelectElement>) => onChangeDatePart(evt, 'MM')}
            isDisabled={isDisabled}
          />

          <FormLabel htmlFor={`${id}-month-input`} fontSize="sm" fontWeight="medium" mb={0}>
            Month
          </FormLabel>
        </Flex>

        <Flex flexDirection="column" flexGrow="1" width="100%" gap={0.5}>
          <Input
            id={`${id}-year-input`}
            name={dobYearName ?? `${name}-year-input`}
            key="year-input"
            data-selector={`${id}year-input-field`}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeDatePart(evt, 'YY')}
            type="text"
            pattern="\d*"
            value={value && value.YYYY !== null ? value.YYYY : ''}
            maxLength={4}
            min={1900}
            placeholder="YYYY"
            onBlur={onBlurYYYY}
            onFocus={onFocus}
            onPaste={(evt: React.ClipboardEvent<HTMLInputElement>) => {
              evt.preventDefault();
              return false;
            }}
            size={size}
            pl={{ base: 3, lg: 4 }}
            pr={{ base: 1, lg: 4 }}
            isDisabled={isDisabled}
            {...inputPropsYY}
          />

          <FormLabel htmlFor={`${id}-year-input`} fontSize="sm" fontWeight="medium" mb={0}>
            Year
          </FormLabel>
        </Flex>

        {canUndo && <UndoEdit id={id} onUndo={onUndo} />}
      </Flex>
    </CustomFormControl>
  );
};

const MonthDayInputWithConfirm = withConfirm(MonthDayInput, 'date-input');

export { MonthDayInput, MonthDayInputWithConfirm };
